import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, SEARCH_KEY } from "@/lib/api";
import { AuthShell, GoogleButton, ROLES, destinationAfterAuth, startGoogle } from "@/pages/Login";

export default function Register() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const search = sp.get("search") || localStorage.getItem(SEARCH_KEY);
  const [role, setRole] = useState(sp.get("role") || (sp.get("search") ? "consumer" : ""));
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const searchForRole = role === "consumer" ? search : null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, role, search_session_id: searchForRole || null });
      if (data.dev_verification_link) sessionStorage.setItem("cz_dev_verify", data.dev_verification_link);
      toast.success("Account created. Check your email to verify your address.");
      setUser(data.user);
      nav(destinationAfterAuth(data.user, searchForRole, null));
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <AuthShell side={search && role === "consumer" ? "Your matches are saved. Create your account to unlock them." : undefined}>
      <h1 className="text-3xl font-bold tracking-tight text-[#003B2D]">Create your free account</h1>
      <p className="text-slate-500 mt-1.5">{searchForRole ? "You'll return straight to your matched providers." : "Choose how you'll use Zunvel."}</p>
      <div className="grid gap-2 mt-6" data-testid="role-selector">
        {ROLES.map((r) => {
          const I = r.icon;
          const on = role === r.key;
          return (
            <button type="button" key={r.key} onClick={() => setRole(r.key)} data-testid={`role-option-${r.key}`}
              className={`flex items-center gap-3 rounded-xl border p-3.5 text-left transition-colors ${on ? "border-teal-600 bg-teal-50 ring-2 ring-teal-600/15" : "border-slate-200 bg-white hover:border-teal-300"}`}>
              <span className={`h-10 w-10 rounded-xl grid place-items-center ${on ? "bg-[#003B2D] text-[#E6C77B]" : "bg-slate-100 text-slate-600"}`}><I className="h-5 w-5" /></span>
              <span className="flex-1"><span className="block font-semibold text-slate-900">{r.title}</span><span className="text-sm text-slate-500">{r.body}</span></span>
              {on && <Check className="h-5 w-5 text-teal-700" />}
            </button>
          );
        })}
      </div>
      {role && (
        <>
          <form onSubmit={submit} className="space-y-4 mt-6" data-testid="register-form">
            <div className="space-y-1.5"><Label>{role === "provider" ? "Your name" : "Full name"}</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" data-testid="register-name-input" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" data-testid="register-email-input" /></div>
            <div className="space-y-1.5"><Label>Password</Label><Input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11" data-testid="register-password-input" /><p className="text-xs text-slate-500">At least 8 characters.</p></div>
            <Button type="submit" className="w-full h-11 rounded-full bg-[#003B2D] hover:bg-[#00281F]" disabled={busy} data-testid="register-submit-btn">{busy ? "Creating…" : searchForRole ? "Create account & unlock matches" : "Create account"}</Button>
          </form>
          <div className="my-5 flex items-center gap-3 text-xs text-slate-400"><div className="h-px bg-slate-200 flex-1" />OR<div className="h-px bg-slate-200 flex-1" /></div>
          <GoogleButton onClick={() => startGoogle(role, searchForRole)} />
        </>
      )}
      <p className="text-xs text-slate-400 mt-6">By creating an account you agree to Zunvel's terms. Zunvel administrator accounts cannot be created publicly.</p>
      <p className="text-sm text-slate-600 mt-4 text-center">Already have an account? <Link to={`/login${search ? `?search=${search}` : ""}`} className="text-teal-700 font-semibold" data-testid="go-login-link">Sign in</Link></p>
    </AuthShell>
  );
}
